import type {
  DefaultError,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';

import type { KibanaLogger } from '@revelio/iso-utility';

import { DataBuilderHttpError } from './errors';

/**
 * Status codes the Data Builder backend uses to surface expected user-input
 * validation feedback. Errors with these statuses should be handled by the caller.
 */
const EXPECTED_VALIDATION_STATUS_CODES: number[] = [
  422, // Unprocessable Entity
];

const expectedValidationStatuses = new Set<number>(
  EXPECTED_VALIDATION_STATUS_CODES
);

const isExpectedValidationError = (error: Error): boolean =>
  error instanceof DataBuilderHttpError &&
  expectedValidationStatuses.has(error.status);

/**
 * Decorates `useMutation` options with the Data Builder mutation policy:
 * opts the mutation out of the global error handler, silences expected
 * errors, and logs everything else to Kibana.
 */
export const withDataBuilderMutationPolicy = <
  TData,
  TVariables,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, Error, TVariables, TContext>,
  kibanaLogger: KibanaLogger | undefined
): UseMutationOptions<TData, Error, TVariables, TContext> => ({
  ...options,
  meta: { ...options.meta, skipGlobalErrorHandling: true },
  onError: (error, variables, context) => {
    const getRequestId = (error: Error): string | undefined =>
      error instanceof DataBuilderHttpError ? error.requestId : undefined;
    if (!isExpectedValidationError(error)) {
      kibanaLogger?.error(`Data Builder mutation failed: ${error.message}`, {
        'request-id': getRequestId(error),
        url: window.location.href,
      });
    }

    options.onError?.(error, variables, context);
  },
});

/**
 * Decorates `useQuery` options with the Data Builder query policy: expected
 * errors are skipped, and logs everything else to Kibana.
 */
export const withDataBuilderQueryPolicy = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> => ({
  ...options,
  meta: {
    ...options.meta,
    expectedErrorStatuses: EXPECTED_VALIDATION_STATUS_CODES,
  },
});
